import { useModalContext } from '@/presentation/context/modal';
import { LiquidityPool, Vault, Zero, matchVault } from 'nftx.js';
import { useTranslations } from 'use-intl';
import VaultIcon from '../../elements/VaultIcon';
import Icon, { Icons } from '../../elements/Icon';
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import Price from '../../elements/Price';
import { ControlTabs } from '../../modules/Tabs';
import useOnClickOutside from '@/presentation/hooks/useOnClickOutside';
import SelectedVault from './SelectedVault';
import Button from '../../elements/Button';
import { useRouter } from 'next/navigation';
import { useNetwork } from '@/presentation/context/network';
import ConnectButton from '../../widgets/ConnectButton';
import FormattedEther from '../../elements/FormattedEther';
import { SelectPoolFormFields } from '../../forms/CreatePositionForms/SelectPoolForm';

export interface CreatePositionProps {
  showLp?: boolean;
  showInventory?: boolean;
  showEth?: boolean;
  showNft?: boolean;
  isNewVault?: boolean;
  vaults?: Vault[];
  vault?: Vault;
  withinModal?: boolean;
  fixedVault: boolean;
  isConnected: boolean;
  setVaultId: Dispatch<SetStateAction<string | undefined>>;
  pools: LiquidityPool[] | undefined;
  poolType?: SelectPoolFormFields['poolType'];
  using?: SelectPoolFormFields['using'];
}

export const CreatePosition = ({
  vaults,
  vault,
  showInventory = true,
  showLp = true,
  showEth = true,
  isNewVault = false,
  withinModal,
  fixedVault,
  setVaultId,
  isConnected,
  pools,
  poolType: initialPoolType,
  using,
}: CreatePositionProps) => {
  const { push } = useRouter();
  const { slug, chain } = useNetwork();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [poolType, setPoolType] = useState<'pair' | 'eth' | 'nft'>(
    initialPoolType || 'pair'
  );
  const [tab, setTab] = useState<'LP' | 'IP'>('LP');
  const [showVaults, setShowVaults] = useState(false);
  const [search, setSearch] = useState('');
  const t = useTranslations('CreatePosition');
  const { showCreateInventoryPositionModal, showCreateLiquidityPositionModal } =
    useModalContext();

  const clickOutsideHandler = useCallback(() => {
    showVaults && setShowVaults(false);
  }, [showVaults, setShowVaults]);
  useOnClickOutside(dropdownRef, clickOutsideHandler);

  const visibileVaults = search
    ? vaults?.filter((x) => matchVault(x, search))
    : vaults;

  const onNextClick = useCallback(() => {
    const vaultId = vault?.vaultId;
    if (vaultId) {
      // replace modal
      if (withinModal) {
        if (tab === 'IP') {
          return showCreateInventoryPositionModal({ vaultId });
        }

        if (tab === 'LP') {
          return showCreateLiquidityPositionModal({
            vaultId,
            poolType,
            using,
          });
        }
      }

      // fullscreen flow
      if (tab === 'IP') {
        return push(`/${slug}/create/position/inventory?vaultId=${vaultId}`);
      }

      if (tab === 'LP') {
        return push(
          `/${slug}/create/position/liquidity?vaultId=${vaultId}&poolType=${poolType}`
        );
      }
    }
  }, [
    tab,
    poolType,
    showCreateInventoryPositionModal,
    showCreateLiquidityPositionModal,
    push,
    vault?.vaultId,
    withinModal,
    slug,
    using,
  ]);

  return (
    <div
      className={`${
        !withinModal ? 'grid' : ''
      } grid-cols-1 md:grid-cols-3 gap-4 md:gap-10`}
    >
      <div className="md:col-span-2 bg-gray-50 border border-gray-300 rounded-2xl p-4 md:p-8">
        <div className="space-y-4">
          <h1 className="text-lg font-bold">{t('title')}</h1>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="bg-gray-100 border border-gray-300 rounded-md p-6 flex items-center w-full"
              onClick={() => setShowVaults((x) => !x)}
              disabled={fixedVault}
            >
              <VaultIcon vaultId={vault?.vaultId} className="w-10 h-10" />
              <span className="mx-4">{vault?.token.name}</span>
              <span className="ml-auto">
                {!fixedVault ? (
                  <Icon
                    name={Icons.CHEVRON_DOWN}
                    className="text-gray-400"
                    size={24}
                  />
                ) : (
                  <Icon
                    name={Icons.CHECK_CIRCLE}
                    className="text-green-500"
                    size={32}
                  />
                )}
              </span>
            </button>
            {showVaults && (
              <div
                className={`overflow-hidden bg-gray-100 border border-gray-300 rounded-md w-full absolute top-full left-0 right-0 ${
                  withinModal ? 'h-64' : 'h-96'
                } mt-4 shadow-lg flex flex-col z-10`}
              >
                <div className="relative m-3">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Icon
                      name={Icons.SEARCH}
                      className="h-5 w-5 text-gray-400 transform transition-transform group-focus-within:scale-105"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    type="search"
                    name="nftx_v3_search"
                    id="nftx_v3_search"
                    className="h-10 md:h-12 bg-gray-200 block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-500 placeholder:focus:text-gray-400 text-gray-500 focus:ring-inset focus:ring-gray-300 text-sm"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />
                </div>
                <div className="flex text-sm font-medium uppercase mt-2 px-3">
                  <div className="w-1/4 p-1">{'Vault'}</div>
                  <div className="w-1/4 p-1 text-right">{'Price'}</div>
                  <div className="w-1/4 p-1 text-right">{'APR'}</div>
                  <div className="w-1/4 p-1 text-right">{'TVL'}</div>
                </div>
                <div className="flex-1 overflow-scroll hide-scroll text-sm">
                  {visibileVaults?.map((v) => {
                    const vaultPools =
                      pools?.filter((pool) => pool.vaultId === v.vaultId) ?? [];
                    const tvl = vaultPools.reduce(
                      (total, pool) => total + pool.totalValueLocked,
                      Zero
                    );
                    const apr = vaultPools.reduce(
                      (best, pool) =>
                        pool.apr['1m'] > best ? pool.apr['1m'] : best,
                      Zero
                    );

                    return (
                      <button
                        key={v.vaultId}
                        className="flex w-full text-left hover:bg-pink-50 hover:dark:bg-black/50 px-3 py-2"
                        type="button"
                        onClick={() => {
                          setVaultId(v.vaultId);
                          setShowVaults(false);
                        }}
                      >
                        <div className="w-1/4 p-1">
                          <div className="flex items-center">
                            <VaultIcon
                              vaultId={v.vaultId}
                              className="w-6 h-6"
                            />
                            <span className="mx-4">{v.token.name}</span>
                          </div>
                        </div>
                        <div className="w-1/4 p-1 text-right">
                          <Price value={v.vTokenToEth} />
                        </div>
                        <div className="w-1/4 p-1 text-right">
                          <FormattedEther value={apr} style="percent" />
                        </div>
                        <div className="w-1/4 p-1 text-right">
                          <Price value={tvl} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <h1 className="text-lg font-medium text-gray-500 mb-4">
            {t('position')}
          </h1>
          <ControlTabs
            activeTab="LP"
            onTabChange={(x: any) => setTab(x)}
            type="text"
            tabs={[
              {
                id: 'LP',
                label: 'Liquidity',
              },
              {
                id: 'IP',
                label: 'Inventory',
              },
            ]}
          />
          {tab === 'IP' && (
            <ControlTabs
              activeTab={'ip'}
              onTabChange={() => void 0}
              tabs={[
                {
                  disabled: true,
                  id: 'lending',
                  label: (
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={Icons.CHART_LINE}
                        size={32}
                        className="text-pink-500"
                      />
                      <h3 className="text-base font-medium flex items-center">
                        {t('lending.title')}
                        <span className="rounded-lg p-1 bg-pink-500 text-white text-xxs ml-1">
                          {'Coming Soon!'}
                        </span>
                      </h3>
                      <p className="normal-case text-xs font-normal">
                        {t('lending.text', { token: vault?.token.symbol })}
                      </p>
                    </div>
                  ),
                },
                {
                  id: 'ip',
                  label: (
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={Icons.PHOTO}
                        size={32}
                        className="text-pink-500"
                      />
                      <h3 className="text-base font-medium">{t('ip.title')}</h3>
                      <p className="normal-case text-xs font-normal">
                        {t('ip.text', { token: vault?.token.symbol })}
                      </p>
                    </div>
                  ),
                },
              ]}
            />
          )}
          {tab === 'LP' && (
            <ControlTabs
              activeTab={poolType}
              onTabChange={(type) => setPoolType(type as any)}
              tabs={[
                {
                  id: 'pair',
                  label: (
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={Icons.SWAP}
                        size={32}
                        className="text-pink-500"
                      />
                      <h3 className="text-base font-medium">{t('lp.title')}</h3>
                      <p className="normal-case text-xs font-normal">
                        {t('lp.text', { token: vault?.token.symbol })}
                      </p>
                    </div>
                  ),
                },
                {
                  id: 'eth',
                  label: (
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={Icons.ETH}
                        size={32}
                        className="text-pink-500"
                      />
                      <h3 className="text-base font-medium">
                        {t('eth.title')}
                      </h3>
                      <p className="normal-case text-xs font-normal">
                        {t('eth.text', { token: vault?.token.symbol })}
                      </p>
                    </div>
                  ),
                },
                {
                  id: 'nft',
                  label: (
                    <div className="flex flex-col items-center space-y-2">
                      <Icon
                        name={Icons.PHOTO}
                        size={32}
                        className="text-pink-500"
                      />
                      <h3 className="text-base font-medium">
                        {t('nft.title')}
                      </h3>
                      <p className="normal-case text-xs font-normal">
                        {t('nft.text', { token: vault?.token.symbol })}
                      </p>
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
        {withinModal && (
          <>
            {isConnected ? (
              <Button onClick={onNextClick} className="w-full mt-6" size="xl">
                {t('SelectedVault.proceed')}
              </Button>
            ) : (
              <ConnectButton className="w-full mt-6" />
            )}
          </>
        )}
      </div>
      {!withinModal && (
        <div className="col-span-1">
          <SelectedVault {...vault} chain={chain} />
          {isConnected ? (
            <Button onClick={onNextClick} className="w-full mt-6" size="xl">
              {t('SelectedVault.proceed')}
            </Button>
          ) : (
            <ConnectButton className="w-full mt-6" />
          )}
        </div>
      )}
    </div>
  );
};

export default CreatePosition;
